/**
 * EVProjectDashboard - Expandable table view matching Site DNA pattern
 */

import React, { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Alert,
    Tabs,
    Tab,
    TableCell,
    TableRow,
    Collapse,
    IconButton,
    Paper,
    alpha,
    useTheme,
} from '@mui/material';
import {
    Add as AddIcon,
    ElectricCar as EVIcon,
    Assessment as AnalysisIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import {
    useThemeMode,
    vortxColors,
    vortxTypography,
    ProjectTableContainer,
    ProjectTableHead,
    ProjectTableHeaderCell,
    ProjectTableBody,
} from '@vortx/shared-ui';
import { retroContainerStyles } from '../../../pages/fluxEVStyles';

function EVProjectDashboard({ projects, error, onCreateProject, onClearError }) {
    const navigate = useNavigate();
    const { mode } = useThemeMode();
    const isRetro = mode === 'retro';
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState('fleet'); // 'fleet' or 'depot'
    const [visibleExpanded, setVisibleExpanded] = useState({}); // tracks which projects are visually expanded

    // Theme-aware colors
    const bgCard = isRetro ? 'rgba(0, 0, 0, 0.4)' : theme.palette.background.paper;
    const textPrimary = isRetro ? '#f4f1ec' : theme.palette.text.primary;
    const textSecondary = isRetro ? '#888' : theme.palette.text.secondary;

    // Filter projects by type
    const fleetProjects = projects.filter(p => p.project_type === 'fleet' || !p.project_type);
    const depotProjects = projects.filter(p => p.project_type === 'depot');

    const currentProjects = activeTab === 'fleet' ? fleetProjects : depotProjects;

    const toggleExpand = (projectId) => {
        setVisibleExpanded(prev => ({
            ...prev,
            [projectId]: !prev[projectId]
        }));
    };

    const isExpanded = (projectId) => {
        return !!visibleExpanded[projectId];
    };

    return (
        <Box sx={isRetro ? retroContainerStyles : {}}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontFamily: vortxTypography.heading,
                        color: isRetro ? vortxColors.gold : 'text.primary',
                    }}
                >
                    Flux EV
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={onClearError}>{error}</Alert>
            )}

            <Box sx={{ borderBottom: 1, borderColor: isRetro ? 'rgba(63, 106, 100, 0.3)' : 'divider', mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, val) => setActiveTab(val)}
                    sx={{
                        '& .MuiTab-root': {
                            color: isRetro ? '#888' : undefined,
                            fontFamily: isRetro ? vortxTypography.mono : undefined,
                        },
                        '& .Mui-selected': {
                            color: isRetro ? vortxColors.teal : undefined,
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: isRetro ? vortxColors.teal : undefined,
                        },
                    }}
                >
                    <Tab
                        label={`Fleet Projects (${fleetProjects.length})`}
                        value="fleet"
                        icon={<EVIcon />}
                        iconPosition="start"
                    />
                    <Tab
                        label={`Depot Analysis (${depotProjects.length})`}
                        value="depot"
                        icon={<AnalysisIcon />}
                        iconPosition="start"
                    />
                </Tabs>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="body2" sx={{ color: textSecondary, fontFamily: isRetro ? vortxTypography.mono : undefined }}>
                    {activeTab === 'fleet'
                        ? 'Model EV charging infrastructure, optimize fleet charging costs, and export load profiles to Site DNA.'
                        : 'Upload depot load data, decompose into charging sessions, and analyze fleet behavior.'
                    }
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => onCreateProject(activeTab)}
                    sx={isRetro ? {
                        bgcolor: 'transparent',
                        color: vortxColors.teal,
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderRadius: 0,
                        border: `1px solid ${vortxColors.teal}`,
                        boxShadow: 'none',
                        '&:hover': {
                            bgcolor: 'rgba(95, 212, 196, 0.1)',
                            boxShadow: `0 0 15px rgba(95, 212, 196, 0.3)`,
                        }
                    } : {
                        bgcolor: vortxColors.teal,
                        '&:hover': {
                            bgcolor: vortxColors.darkGreen,
                        },
                    }}
                >
                    {activeTab === 'fleet' ? 'New Fleet Project' : 'New Depot Project'}
                </Button>
            </Box>

            {currentProjects.length === 0 ? (
                <Paper sx={isRetro ? {
                    p: 4,
                    textAlign: 'center',
                    bgcolor: 'rgba(0, 0, 0, 0.4)',
                    borderRadius: 0,
                    border: '1px solid rgba(63, 106, 100, 0.3)',
                } : {
                    p: 4,
                    textAlign: 'center'
                }}>
                    {activeTab === 'fleet' ? (
                        <EVIcon sx={{ fontSize: 64, color: textSecondary, mb: 2 }} />
                    ) : (
                        <AnalysisIcon sx={{ fontSize: 64, color: textSecondary, mb: 2 }} />
                    )}
                    <Typography variant="h6" gutterBottom sx={{ color: textPrimary, fontFamily: isRetro ? vortxTypography.mono : undefined }}>
                        {activeTab === 'fleet' ? 'No fleet projects yet' : 'No depot analysis projects yet'}
                    </Typography>
                    <Typography sx={{ mb: 3, color: textSecondary, fontFamily: isRetro ? vortxTypography.mono : undefined }}>
                        {activeTab === 'fleet'
                            ? 'Create your first project to start modeling EV charging infrastructure.'
                            : 'Create your first depot project to analyze existing charging data.'
                        }
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => onCreateProject(activeTab)}
                        sx={isRetro ? {
                            bgcolor: 'transparent',
                            color: vortxColors.teal,
                            fontFamily: vortxTypography.mono,
                            border: `1px solid ${vortxColors.teal}`,
                            '&:hover': {
                                bgcolor: 'rgba(95, 212, 196, 0.1)',
                            }
                        } : {}}
                    >
                        Create {activeTab === 'fleet' ? 'Fleet' : 'Depot'} Project
                    </Button>
                </Paper>
            ) : (
                <ProjectTableContainer>
                    <ProjectTableHead>
                        <ProjectTableHeaderCell sx={{ width: 50 }} />
                        <ProjectTableHeaderCell>Project</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell>Details</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell align="right">Actions</ProjectTableHeaderCell>
                    </ProjectTableHead>
                    <ProjectTableBody>
                            {currentProjects.map((project) => {
                                const isDepot = project.project_type === 'depot';
                                const depotResult = project.depot_config?.decomposition_result;

                                return (
                                    <Fragment key={project.id}>
                                        {/* Project Row */}
                                        <TableRow
                                            onClick={() => navigate(`/ev/${project.id}`)}
                                            sx={isRetro ? {
                                                cursor: 'pointer',
                                                bgcolor: isExpanded(project.id) ? 'rgba(178, 140, 80, 0.1)' : 'rgba(0, 0, 0, 0.4)',
                                                borderLeft: isExpanded(project.id) ? `3px solid ${vortxColors.gold}` : '3px solid transparent',
                                                '&:hover': {
                                                    bgcolor: 'rgba(178, 140, 80, 0.15)',
                                                },
                                                '& td': {
                                                    borderBottom: '1px solid rgba(63, 106, 100, 0.3)',
                                                }
                                            } : {
                                                cursor: 'pointer',
                                                bgcolor: isExpanded(project.id) ? alpha(vortxColors.gold, 0.08) : 'transparent',
                                                '&:hover': {
                                                    bgcolor: alpha(vortxColors.gold, 0.1),
                                                },
                                            }}
                                        >
                                            <TableCell sx={{ width: 50 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleExpand(project.id);
                                                    }}
                                                    sx={{ color: isRetro ? '#5fd4c4' : vortxColors.gold }}
                                                >
                                                    {isExpanded(project.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {isDepot ? (
                                                        <AnalysisIcon sx={{ color: isRetro ? '#5fd4c4' : vortxColors.teal }} />
                                                    ) : (
                                                        <EVIcon sx={{ color: isRetro ? '#5fd4c4' : vortxColors.teal }} />
                                                    )}
                                                    <Typography sx={{
                                                        color: textPrimary,
                                                        fontWeight: 500,
                                                        fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                                        fontSize: isRetro ? '0.85rem' : undefined,
                                                    }}>
                                                        {project.name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography sx={{
                                                    color: textSecondary,
                                                    fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                                    fontSize: isRetro ? '0.75rem' : '0.875rem',
                                                }}>
                                                    {isDepot ? (
                                                        depotResult
                                                            ? `${depotResult.fleet_size} vehicles • ${depotResult.sessions?.length || 0} sessions`
                                                            : 'Not yet analyzed'
                                                    ) : (
                                                        `${project.fleet?.length || 0} vehicle groups • ${project.chargers?.length || 0} chargers`
                                                    )}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/ev/${project.id}`);
                                                    }}
                                                    sx={isRetro ? {
                                                        color: '#5fd4c4',
                                                        fontFamily: vortxTypography.mono,
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600,
                                                        textTransform: 'uppercase',
                                                        borderRadius: 0,
                                                        '&:hover': {
                                                            bgcolor: 'rgba(95, 212, 196, 0.1)',
                                                        }
                                                    } : {
                                                        color: vortxColors.teal,
                                                        fontWeight: 600,
                                                        '&:hover': {
                                                            bgcolor: alpha(vortxColors.teal, 0.1),
                                                        }
                                                    }}
                                                >
                                                    Open
                                                </Button>
                                            </TableCell>
                                        </TableRow>

                                        {/* Expanded Details Row */}
                                        <TableRow>
                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
                                                <Collapse in={isExpanded(project.id)} timeout="auto" unmountOnExit>
                                                    <Box sx={{
                                                        py: 2,
                                                        px: 3,
                                                        bgcolor: isRetro ? 'rgba(0, 0, 0, 0.2)' : alpha(vortxColors.warmStone, 0.3),
                                                    }}>
                                                        {isDepot ? (
                                                            // Depot project details
                                                            depotResult ? (
                                                                <Box>
                                                                    <Typography variant="subtitle2" sx={{
                                                                        color: textPrimary,
                                                                        fontWeight: 600,
                                                                        mb: 1.5,
                                                                        fontFamily: isRetro ? vortxTypography.mono : undefined,
                                                                    }}>
                                                                        Decomposition Results
                                                                    </Typography>
                                                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                                                                        <Box>
                                                                            <Typography variant="caption" sx={{ color: textSecondary, fontFamily: isRetro ? vortxTypography.mono : undefined }}>
                                                                                Fleet Size
                                                                            </Typography>
                                                                            <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 500, fontFamily: isRetro ? vortxTypography.mono : undefined }}>
                                                                                {depotResult.fleet_size} vehicles
                                                                            </Typography>
                                                                        </Box>
                                                                        <Box>
                                                                            <Typography variant="caption" sx={{ color: textSecondary, fontFamily: isRetro ? vortxTypography.mono : undefined }}>
                                                                                Sessions
                                                                            </Typography>
                                                                            <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 500, fontFamily: isRetro ? vortxTypography.mono : undefined }}>
                                                                                {depotResult.sessions?.length || 0}
                                                                            </Typography>
                                                                        </Box>
                                                                        <Box>
                                                                            <Typography variant="caption" sx={{ color: textSecondary, fontFamily: isRetro ? vortxTypography.mono : undefined }}>
                                                                                Reconstruction R²
                                                                            </Typography>
                                                                            <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 500, fontFamily: isRetro ? vortxTypography.mono : undefined }}>
                                                                                {depotResult.validation?.reconstruction_r_squared?.toFixed(3) || 'N/A'}
                                                                            </Typography>
                                                                        </Box>
                                                                        {depotResult.avg_session_energy_kwh && (
                                                                            <Box>
                                                                                <Typography variant="caption" sx={{ color: textSecondary, fontFamily: isRetro ? vortxTypography.mono : undefined }}>
                                                                                    Avg Session Energy
                                                                                </Typography>
                                                                                <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 500, fontFamily: isRetro ? vortxTypography.mono : undefined }}>
                                                                                    {depotResult.avg_session_energy_kwh.toFixed(1)} kWh
                                                                                </Typography>
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            ) : (
                                                                <Typography variant="body2" sx={{ color: textSecondary, fontStyle: 'italic', fontFamily: isRetro ? vortxTypography.mono : undefined }}>
                                                                    Upload depot load data to see analysis results
                                                                </Typography>
                                                            )
                                                        ) : (
                                                            // Fleet project details
                                                            <Box>
                                                                <Typography variant="subtitle2" sx={{
                                                                    color: textPrimary,
                                                                    fontWeight: 600,
                                                                    mb: 1.5,
                                                                    fontFamily: isRetro ? vortxTypography.mono : undefined,
                                                                }}>
                                                                    Project Configuration
                                                                </Typography>
                                                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                                                                    <Box>
                                                                        <Typography variant="caption" sx={{ color: textSecondary, fontFamily: isRetro ? vortxTypography.mono : undefined }}>
                                                                            Vehicle Groups
                                                                        </Typography>
                                                                        <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 500, fontFamily: isRetro ? vortxTypography.mono : undefined }}>
                                                                            {project.fleet?.length || 0}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box>
                                                                        <Typography variant="caption" sx={{ color: textSecondary, fontFamily: isRetro ? vortxTypography.mono : undefined }}>
                                                                            Chargers
                                                                        </Typography>
                                                                        <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 500, fontFamily: isRetro ? vortxTypography.mono : undefined }}>
                                                                            {project.chargers?.length || 0}
                                                                        </Typography>
                                                                    </Box>
                                                                    {project.site_config?.location && (
                                                                        <Box>
                                                                            <Typography variant="caption" sx={{ color: textSecondary, fontFamily: isRetro ? vortxTypography.mono : undefined }}>
                                                                                Location
                                                                            </Typography>
                                                                            <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 500, fontFamily: isRetro ? vortxTypography.mono : undefined }}>
                                                                                {project.site_config.location}
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </Fragment>
                                );
                            })}
                    </ProjectTableBody>
                </ProjectTableContainer>
            )}
        </Box>
    );
}

export default EVProjectDashboard;
